import { AfterViewInit, Component, inject, Input } from "@angular/core";
import { TradableActionButtonsComponent } from "./tradable-action-buttons.component";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { StockTestService } from "@main-services/stock-test.service";
import { WalletSelectionModalComponent } from "../../../main/components/tradable-to-view-item/tradable-to-wallet/wallet-selection-modal/wallet-selection-modal.component";
import { AccountService } from "../../../account/services/account.service";
import { TooltipService, TooltipTradableAction } from "@main-services/tooltip.service";
import { UserService } from "@main-services/user.service";
import { Modal, ModalService } from "@core-services/modal.service";
import { TranslationService } from "@core-services/translation.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { Best2TermsModalComponent } from "../../../trade/modals/best-2-terms-modal/components/best-2-terms-modal.component";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { ActivatedRoute, Router } from "@angular/router";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { InfoService } from "@core-services/info.service";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { StakedAssetFacade } from "@modules/staking/domain/+state/staked-asset.facade";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { StakingAmountValidationService } from "@modules/staking/domain/+state/utility/staking-amount-validation.service";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { combineLatest, take } from "rxjs";
import { CryptoDepositFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit.facade";
import { CryptoDepositTrackingFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-tracking.facade";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { ActionButtonsFacade } from "@modules/discover/domain/+state/action-buttons.facade";
import { ButtonAction } from "@modules/discover/constants/actions";
import { StakingEntryHelperService } from "@modules/staking/services/staking-entry.helper.service";

@Component({
	selector: "detail-page-action-buttons",
	templateUrl: "./tradable-action-buttons.component.html",
	styleUrls: ["./tradable-action-buttons.component.scss"],
	providers: [CryptoDepositFacade],
	standalone: false
})
export class DetailPageActionButtonsComponent extends TradableActionButtonsComponent implements AfterViewInit {
	protected readonly termsAndConditionsService = inject(TermsAndConditionsService);
	protected readonly stakingEntryHelper = inject(StakingEntryHelperService);
	@Input() public tradable: Tradable = null;
	@Input() public stakeable: Stakeable = null;
	protected readonly cryptoDepositTrackingFacade = inject(CryptoDepositTrackingFacade);
	private dynamictermAndConditionActive = false;

	public constructor(
		protected readonly modalService: ModalService,
		protected readonly accountService: AccountService,
		protected readonly tradableListService: TradableListService,
		protected readonly tradableWalletListService: TradableWalletListService,
		protected readonly translationService: TranslationService,
		protected readonly fiatWalletService: FiatWalletService,
		protected readonly tooltipService: TooltipService,
		protected readonly userService: UserService,
		protected readonly stockTestService: StockTestService,
		protected readonly stockTestHelperService: StockTestHelperService,
		protected readonly stakingFacade: StakingFacade,
		protected readonly router: Router,
		protected readonly route: ActivatedRoute,
		protected readonly infoService: InfoService,
		protected readonly sendEntryPoint: SendEntryPointService,
		protected readonly stakedAssetFacade: StakedAssetFacade,
		protected readonly userFiatFundsService: UserFiatFundsService,
		protected readonly fiatDepositEntryService: FiatDepositEntryService,
		protected readonly stakingAmountValidationService: StakingAmountValidationService,
		protected readonly launchDarklyService: LaunchDarklyService,
		protected readonly cryptoDepositFacade: CryptoDepositFacade,
		protected readonly checkoutEntryHelper: CheckoutEntryHelperService,
		protected readonly limitOrdersTrackingFacade: LimitOrdersTrackingFacade,
		protected readonly actionButtonsFacade: ActionButtonsFacade
	) {
		super(
			modalService,
			accountService,
			tradableWalletListService,
			translationService,
			fiatWalletService,
			tooltipService,
			userService,
			stockTestService,
			stockTestHelperService,
			stakingFacade,
			userFiatFundsService,
			fiatDepositEntryService,
			stakedAssetFacade,
			sendEntryPoint,
			launchDarklyService,
			checkoutEntryHelper,
			cryptoDepositFacade,
			limitOrdersTrackingFacade,
			actionButtonsFacade
		);
	}

	public ngAfterViewInit(): void {
		this.termsAndConditionsService.isDynamictermAndConditionActive$.subscribe((isActive) => {
			this.dynamictermAndConditionActive = isActive;
		});
	}

	// BEST 2.0 opt-in terms
	public onActionClick(action: string, tradableWallet: TradableWallet = null): void {
		if (action === TooltipTradableAction.LEVERAGE) {
			this.actionButtonsFacade.performAction(ButtonAction.MARGIN_TRADE, this.tradable, null);
			if (this.marginTradingFacade.showMarginTradingRedDot$.getValue()) {
				this.marginTradingFacade.setShowMarginTradingRedDot(false);
			}

			return;
		}

		if (
			!this.dynamictermAndConditionActive &&
			(this.tradable?.getId() === TOKEN_BEST_ID || tradableWallet?.getCurrencyId() === TOKEN_BEST_ID) &&
			this.accountService.showBest2TermsOnBestTrade() &&
			["buy", "deposit"].some((a) => a === action)
		) {
			this.handleBest2Terms(action, tradableWallet);
			return;
		}

		if (action === TRADE_TYPE.STAKE || action === TRADE_TYPE.UNSTAKE) {
			// In case there is no balance in default wallet, clicking the stake-button will pop an error message
			if (action === TRADE_TYPE.STAKE) {
				const stakeableWallet: TradableWallet = this.tradableWalletListService
					.getWalletsByTradableId(this.tradable.getId())
					.find((wallet) => wallet.isDefault());

				// first check if stakeableWallet & stakeable is available
				if (!!stakeableWallet && this.stakeable) {
					const isStakeable: boolean =
						!this.stakingAmountValidationService.hasFundsToStake(this.stakeable) ||
						this.stakeable.availability === StakeableAvailability.SOLD_OUT;

					// then check if there is enough fund to perform staking
					if (isStakeable) {
						this.infoService.setError(
							this.translationService.translate("stake_buy_more_toaster", [this.tradable.getName()])
						);
						return;
					}
				}
			}

			this.stakingFacade.setWalletByTradableId(this.tradable.getId());
			this.stakingEntryHelper.openStakingFlow(this.tradable.getId(), action);
			return;
		}

		this.wallet = tradableWallet ? tradableWallet : this.wallet;
		this.tradable = this.tradableListService.getItemById(this.wallet.getCurrencyId());

		if (action === TooltipTradableAction.TRANSFER) {
			this.updateAssetWalletsForTransfer();
		}

		let walletList: TradableWallet[] = this.availableWallets(action);

		// BUY starts here
		combineLatest([
			this.launchDarklyService.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED),
			this.launchDarklyService.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN)
		])
			.pipe(take(1))
			.subscribe(([checkoutEnabled, multichainEnabled]) => {
				if (action === TooltipTradableAction.LIMIT_ORDER) {
					this.limitOrdersTrackingFacade.updateFlow(OrderFlow.AdpMore);
					this.limitOrdersService.openOrderTypeDecision(this.tradable?.getId());
					return;
				}

				if (action === TRADE_TYPE.SELL || action === TRADE_TYPE.BUY) {
					this.limitOrdersTrackingFacade.updateFlow(OrderFlow.Adp);
				}
				if (checkoutEnabled && action === TRADE_TYPE.BUY) {
					// New CHECKOUT BUY flow
					this.modalService.pop();

					const assetId = this.tradable?.getId();
					this.checkoutEntryHelper.onTradableSelect(assetId, undefined, this.dynamictermAndConditionActive);
				} else {
					if (multichainEnabled && action === TRADE_TYPE.DEPOSIT && !this.dynamictermAndConditionActive) {
						//multichain is enabled here, we add our own logic
						if (
							this.tradable.getId() === TOKEN_BEST_ID &&
							this.accountService.showBest2TermsOnBestTrade()
						) {
							const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, {
								showBackdrop: false
							});
							best2TermsModal.present();
							best2TermsModal.onDismiss.subscribe((res) => {
								if (res.data === "accepted") {
									this.navigateToNextScreen();
								}
							});
						} else {
							this.navigateToNextScreen();
						}
					}
					// Legacy BUY flow
					else {
						// If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
						if (walletList.length > 1) {
							walletList = walletList.filter((w) => !w.isNew());
						}

						// If we still have more than 1 wallet, then we need to show the multiple wallets dialog
						if (walletList.length > 1) {
							const selectionModal: Modal<WalletSelectionModalComponent> = this.modalService.create(
								WalletSelectionModalComponent,
								{
									tradableWallets: walletList,
									tradableId: this.tradable.getId()
								}
							);
							selectionModal.present();
							selectionModal.onDismiss.subscribe((res) => {
								const selectedWallet: TradableWallet = res["data"];
								if (!selectedWallet) {
									return;
								}
								super.onActionClick(action, selectedWallet, this.dynamictermAndConditionActive);
							});
						} else {
							super.onActionClick(action, walletList[0], this.dynamictermAndConditionActive);
						}
					}
				}
			});
	}

	private navigateToNextScreen(): void {
		this.cryptoDepositFacade.availableCryptoList$.pipe(take(1)).subscribe((list) => {
			const currentDepositCrypto = list.find(
				(item) => item.symbol.toUpperCase() === this.tradable.getAssetSymbol().toUpperCase()
			);
			if (currentDepositCrypto) {
				this.cryptoDepositTrackingFacade.trackDepositFlowStarted();
				if (currentDepositCrypto.networks.length > 1) {
					this.cryptoDepositFacade.navigateToNetworkSelection(currentDepositCrypto.symbol);
				} else {
					this.cryptoDepositFacade.navigateToDepositSummary(
						currentDepositCrypto.symbol,
						currentDepositCrypto.networks[0].id
					);
				}
			}
		});
	}

	public availableWallets(action: string): TradableWallet[] {
		return this.tradableWalletListService
			.getWalletsByTradableId(this.tradable.getId())
			.filter((wallet) => !this.tooltipService.isDisabled(action, this.tradable, this.fiat, wallet));
	}

	// BEST 2.0 opt-in terms
	public handleBest2Terms(action: string, wallet: TradableWallet): void {
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.onActionClick(action, wallet);
			}
		});
	}
}
